import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { logout } from '../../features/authSlice';
import { mainServiceURL } from './index';

const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  credentials: 'include',
});

// Debounce the eventual redirect: RTK often fires many concurrent queries
// (jobs poll, credit balance, favourites) — if the access token expired,
// each independently 401s. Without the debounce we'd try to refresh N times
// AND dispatch logout N times in the same tick.
let refreshInFlight: Promise<{ ok: boolean }> | null = null;
let handlingLogout = false;

async function refreshOnce(api: any, extraOptions: any): Promise<{ ok: boolean }> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = baseQuery(
    { url: `${mainServiceURL}/login/native/refresh`, method: 'POST' },
    api,
    extraOptions,
  ).then((r) => ({ ok: !!r.data }))
    .finally(() => {
      // Clear the in-flight lock on the next tick so late callers see a
      // fresh state, but concurrent callers this tick share the same result.
      setTimeout(() => { refreshInFlight = null; }, 0);
    });
  return refreshInFlight;
}

function softLogoutRedirect(): void {
  if (handlingLogout) return;
  handlingLogout = true;
  // Preserve the current URL (path + query) so LoginPage can return here
  // after a successful re-login. GeneratorPage in particular loses its
  // prefilled reference chip if we drop it, which is the worst UX.
  const next = `${window.location.pathname}${window.location.search}`;
  const already = window.location.pathname === '/login' || window.location.pathname === '/';
  setTimeout(() => {
    if (!already) {
      window.location.assign(`/login?next=${encodeURIComponent(next)}&reason=expired`);
    }
    handlingLogout = false;
  }, 0);
}

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions);

  const status = result.meta?.response?.status;
  if (result.error && (status === 401 || status === 403)) {
    // Don't try to refresh the refresh endpoint itself — infinite loop.
    const argsUrl = typeof args === 'string' ? args : args.url;
    const isRefreshCall = typeof argsUrl === 'string' && argsUrl.includes('/login/native/refresh');
    if (isRefreshCall) {
      api.dispatch(logout());
      softLogoutRedirect();
      return result;
    }
    const refreshed = await refreshOnce(api, extraOptions);
    if (refreshed.ok) {
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
      softLogoutRedirect();
    }
  }

  return result;
};
